import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppRole, Profile, UserRole } from '@/lib/supabase-types';
import { Sentry } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRole: AppRole | null;
  loading: boolean;
  profileLoaded: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, redirectUrl?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  setUserRole: (role: AppRole) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRole, setUserRoleState] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  // True once the profile + role lookup for the current session has settled.
  // Route guards must wait for this so a deep link isn't bounced to
  // role-select before the user's role is known.
  const [profileLoaded, setProfileLoaded] = useState(false);
  const queryClient = useQueryClient();
  // Guard against double-fetch when onAuthStateChange and getSession both fire
  const fetchingForRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string, userEmail?: string, userFullName?: string) => {
    if (fetchingForRef.current === userId) return;
    fetchingForRef.current = userId;
    try {
      console.log('[useAuth] Fetching profile for user:', userId);
      let { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (profileError) {
        console.error('[useAuth] Profile fetch error:', profileError);
      } else if (!profileData) {
        // Profile doesn't exist - create one for this authenticated user
        console.log('[useAuth] No profile found, creating one...');
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({
            user_id: userId,
            email: userEmail || null,
            full_name: userFullName || null,
          })
          .select()
          .single();
        
        if (createError) {
          console.error('[useAuth] Profile creation error:', createError);
          // Check for zombie session - user exists in JWT but not in auth.users database
          if (createError.message.includes('foreign key constraint') || createError.code === '23503') {
            console.warn('[useAuth] Zombie session detected - user not in database, clearing auth state');
            localStorage.removeItem('sb-lweauudwylsfnlcftrjz-auth-token');
            sessionStorage.removeItem('pending_role');
            sessionStorage.removeItem('just_completed_onboarding');
            await supabase.auth.signOut();
            setProfile(null);
            setUserRoleState(null);
            setLoading(false);
            return; // Exit early, don't continue with role fetching
          }
        } else {
          console.log('[useAuth] Profile created:', newProfile?.id);
          profileData = newProfile;
        }
      } else {
        console.log('[useAuth] Profile fetched:', profileData?.id);
      }
      setProfile(profileData as Profile | null);

      // Tag Sentry with user identity (userId only — no PII beyond what they've consented to)
      if (profileData) {
        Sentry.setUser({ id: userId });
      }

      // Use .limit(1) instead of .maybeSingle() since user may have multiple roles
      // Order by created_at to get the first role they signed up with
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })
        .limit(1);
      
      if (roleError) {
        console.error('[useAuth] Role fetch error:', roleError);
        setUserRoleState(null);
      } else {
        const firstRole = roleData && roleData.length > 0 ? roleData[0] : null;
        console.log('[useAuth] Role fetched:', firstRole?.role);
        setUserRoleState((firstRole as UserRole | null)?.role || null);
        // Tag Sentry with user role for filtering errors by role
        if (firstRole?.role) {
          Sentry.setTag('user_role', firstRole.role);
        }
      }
    } catch (error) {
      console.error('[useAuth] Error fetching profile:', error);
    } finally {
      fetchingForRef.current = null;
      setProfileLoaded(true);
    }
  };

  useEffect(() => {
    let initialFetchDone = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Skip INITIAL_SESSION as getSession handles the initial load
        if (event === 'INITIAL_SESSION') return;

        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Use setTimeout to avoid Supabase deadlock
          setTimeout(() => fetchProfile(
            session.user.id,
            session.user.email,
            session.user.user_metadata?.full_name
          ), 0);
        } else {
          setProfile(null);
          setUserRoleState(null);
          setProfileLoaded(true);
        }
        setLoading(false);
      }
    );

    // THEN check for existing session (handles initial load only)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (initialFetchDone) return;
      initialFetchDone = true;
      
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(
          session.user.id,
          session.user.email,
          session.user.user_metadata?.full_name
        );
      } else {
        setProfileLoaded(true);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string, redirectUrl?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl ?? window.location.origin,
        data: { full_name: fullName },
      },
    });
    return { error: error as Error | null };
  };


  const signOut = async () => {
    // Clear any pending role from sessionStorage to prevent auto-redirect loops
    sessionStorage.removeItem('pending_role');
    // Clear tutorial/onboarding flags to prevent stale state on next login
    sessionStorage.removeItem('just_completed_onboarding');
    // Clear selected child from localStorage to prevent auto-selection on next login
    localStorage.removeItem('unlocked-tutoring-selected-child');
    await supabase.auth.signOut();
    // Clear React Query cache so previous user's data never flashes for the next user
    queryClient.clear();
    setProfile(null);
    setUserRoleState(null);
    // Clear Sentry user context on sign-out
    Sentry.setUser(null);
    Sentry.setTag('user_role', null);
  };

  const setUserRoleFunc = async (role: AppRole) => {
    if (!user) return { error: new Error('Not authenticated') };

    // Validate role on client side (server-side RLS also enforces this)
    if (role !== 'student' && role !== 'parent') {
      return { error: new Error('Invalid role selection') };
    }

    // Insert role - RLS policy restricts to student/parent only
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: user.id, role });

    if (error) {
      // Foreign key error means user doesn't exist in auth.users - zombie session
      if (error.message.includes('foreign key constraint') || error.code === '23503') {
        // Clear zombie session
        localStorage.removeItem('sb-lweauudwylsfnlcftrjz-auth-token');
        sessionStorage.removeItem('pending_role');
        await supabase.auth.signOut();
        setProfile(null);
        setUserRoleState(null);
        return { error: new Error('Your session has expired. Please sign up again.') };
      }
      return { error: error as Error };
    }

    setUserRoleState(role);

    // Create role-specific profile (use upsert to avoid duplicates)
    if (role === 'student') {
      await supabase.from('students').upsert(
        { user_id: user.id },
        { onConflict: 'user_id' }
      );
    } else if (role === 'parent') {
      await supabase.from('parents').upsert(
        { user_id: user.id },
        { onConflict: 'user_id' }
      );
    }

    return { error: null };
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(
        user.id,
        user.email,
        user.user_metadata?.full_name
      );
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-password-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return { error: new Error(errorData.error || 'Failed to send reset email') };
      }

      return { error: null };
    } catch (err) {
      console.error('[useAuth] Password reset error:', err);
      return { error: err as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        userRole,
        loading,
        profileLoaded,
        signIn,
        signUp,
        signOut,
        setUserRole: setUserRoleFunc,
        refreshProfile,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
